/*
 * aricove-pdp-c-variant.js
 * Gedeelde variant-controller voor het nieuwe PDP-component-systeem.
 * Vervangt vier inline implementaties:
 *   - snippets/aricove-pdp-picker.liquid  (WB, 2-traps maat×gewicht)
 *   - sections/aricove-pdp-cover-hero.liquid (kleur×maat)
 *   - sections/aricove-pdp-kids-hero.liquid (kleur×maat of 1-as maat)
 *   - sections/aricove-pdp-bed-hero.liquid  (1-as maat, #size_-filter)
 *
 * Publiek contract (ONGEWIJZIGD -- de cart-laag hangt eraan):
 *   window.__apdpVariant = { id, opt, price, compare, label, avail, dim, lb, color, inv }
 *   document.dispatchEvent(new CustomEvent('apdp:variant', { detail }))
 *   document.addEventListener('apdp:setvariant', e => ...) met { id }
 *
 * Intern:
 *   window.__apdpc = { get(), set(id), setAxis(axis, value) }
 *
 * Guard: window.__apdpcVariantBound per [data-apdpc-hero], niet per PDP.
 * Geladen met defer; ES5-compatibel, geen optional chaining, geen modules.
 *
 * Attributen op [data-apdpc-hero] die de BEGINSTAND sturen (W4):
 *   data-apdpc-default  -- de variant die bij een verse load geselecteerd staat.
 *   data-apdpc-popular  -- de variant met de "Most picked"-badge; stuurt ook de
 *                          auto-snap na een maatwissel (C74/C78).
 * Twee attributen omdat het twee vragen zijn. Ze mogen uiteenlopen en doen dat
 * op de WB ook: default Twin 12, populair Full 15. Beide leeg = het gedrag van
 * voor W4.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Maatfamilie-map (GESLOTEN, BMDC 18-08)                              */
  /* Bron: CONTRACT §7 + aricove-pdp-hero.liquid r.277 + m-gallery C53.  */
  /* ------------------------------------------------------------------ */
  var DIM_TO_KEY = {
    '48"x72"':  'twin',
    '60"x80"':  'full',
    '80"x87"':  'queen',
    '88"x104"': 'king'
  };

  /* ------------------------------------------------------------------ */
  /* Hulpfuncties                                                         */
  /* ------------------------------------------------------------------ */

  /* Formatteert centbedrag naar '$1,234' of '$1,234.56'.
   * .replace('.00','') spiegelt money_without_trailing_zeros (§2.8). */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      .replace(/\.00$/, '');
  }

  /* Volledige centnotatie, spiegel van het Liquid-filter `money` ($96.95,
   * en $80.00 blijft $80.00). Bewust NAAST money(): die stript '.00' omdat
   * hij money_without_trailing_zeros spiegelt voor de Save-badge. De ATC-knop
   * en de prijsregel gebruiken live wel de volle notatie; met money() zou de
   * knop na een variantwissel van "$80.00" naar "$80" springen terwijl de
   * server-render "$80.00" was. */
  function moneyFull(cents) {
    return '$' + (cents / 100).toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ------------------------------------------------------------------ */
  /* Per-hero initialisatie                                               */
  /* ------------------------------------------------------------------ */
  function initHero(hero) {
    /* Guard per component zodat een tweede render de handlers niet verdubbelt.
     * Bron: CONTRACT §7 "per component, niet per PDP". */
    if (hero.__apdpcVariantBound) return;
    hero.__apdpcVariantBound = true;

    /* JSON-matrix lezen. Bij een parse-fout gaat de rest van het blok defensief
     * door dankzij de lege array -- fill/bp hebben geen picker maar wél een seed. */
    var matrix = [];
    var jsonEl = hero.querySelector('[data-apdpc-variants]');
    if (jsonEl) {
      try { matrix = JSON.parse(jsonEl.textContent || jsonEl.innerText || '[]'); } catch (e) {}
    }

    /* ---------------------------------------------------------------- */
    /* As-herkenning                                                      */
    /* ---------------------------------------------------------------- */
    /* Bepaal het type picker op basis van de optienamen in de matrix.
     * Vier gevallen (§2.6 NOW-components):
     *   NONE   -- 0/1 variant zonder opties (fill, bp)
     *   SIZE1  -- 1 optie, ALLEEN maat-string (bed: Queen/King)
     *   WB     -- 1 optie met maat+gewicht in één string ('60"x80" 15lb')
     *   COLOR  -- 2 opties: kleur (o1) × maat (o2)
     *
     * Detectie op de WAARDE, niet op de aanwezigheid van de veldnaam. Dat is geen
     * detail: c-picker emit elke rij met ALLE sleutels ingevuld, ontbrekende assen
     * als null (gemeten op de bed-preview 21-08: {o1:null, o2:null, color:null,
     * lb:null, dim:null, size:"Queen"}). Met `typeof x !== 'undefined'` was hasColor
     * daardoor ALTIJD waar en belandde bed in de COLOR-tak in plaats van SIZE1 --
     * met als zichtbaar gevolg dat de galerij niet op maat filterde. `!= null`
     * vangt null en undefined allebei. */
    var hasLb = matrix.length > 0 && matrix[0].lb != null;
    var hasColor = matrix.length > 0 && (matrix[0].color != null || matrix[0].o1 != null);
    var hasSize  = matrix.length > 0 && (matrix[0].size  != null || matrix[0].o2 != null);

    /* Als hasLb: elke rij heeft ook een dim -> 2-traps maat+gewicht (WB). */
    var mode;
    if (matrix.length <= 1 && !hasLb && !hasColor) {
      mode = 'NONE';
    } else if (hasLb) {
      /* Detecteer of de `lb`-waarde getal is en de `dim`-veld er ook bij zit.
       * 'opt' bij WB = '60"x80" 15lb'; parse dim+lb als ze niet al voorberekend zijn. */
      matrix.forEach(function (v) {
        if (!v.dim || !v.lb) {
          var m = String(v.opt || '').match(/^(.*?)\s+(\d+)\s*lb$/i);
          if (m) { v.dim = m[1].trim(); v.lb = parseInt(m[2], 10); }
        }
      });
      mode = 'WB';
    } else if (hasColor) {
      /* Normaliseer color/size van o1/o2 zodat de rest van de code één veldnaam gebruikt. */
      matrix.forEach(function (v) {
        if (typeof v.color === 'undefined') v.color = v.o1 || null;
        if (typeof v.size  === 'undefined') v.size  = v.o2 || null;
      });
      mode = 'COLOR';
    } else {
      /* 1 as: maat of andere enkelvoudige optie (bed). Zet size = opt of o1. */
      matrix.forEach(function (v) {
        if (typeof v.size === 'undefined') v.size = v.opt || v.o1 || null;
      });
      mode = 'SIZE1';
    }

    /* ---------------------------------------------------------------- */
    /* C84 -- gecollapste assen                                           */
    /* ---------------------------------------------------------------- */
    /* Als maat en gewicht 1 op 1 correleren rendert c-picker ÉÉN stap met het
     * gecombineerde label ('36"x48" 5lb') en GEEN gewicht-stap. De beslissing
     * valt server-side in c-picker (collapse-detectie op de variantmatrix); de
     * JS neemt hem hier over via het `combo`-veld in plaats van hem nog eens
     * zelf af te leiden. Eén waarheid: wijkt de JS af, dan matcht de maat-tegel
     * niet meer op zijn eigen data-apdpc-size.
     *
     * `combo` is alleen gevuld als de collapse geldt, dus deze vlag is precies
     * dezelfde boolean als collapse_axes in de Liquid. Op WB is combo overal
     * null (20lb deelt drie maten) en blijft alles bij het oude. */
    var collapsed = mode === 'WB' && matrix.length > 0 && matrix[0].combo != null;

    /* De maat-as-waarde van een rij: bij collapse de volle optiestring, anders
     * de dim (WB) of size (COLOR/SIZE1). Eén functie, zodat sync, resolve en de
     * buybar-dropdown gegarandeerd dezelfde sleutel gebruiken. */
    function axisSize(v) {
      if (!v) return null;
      if (collapsed) return v.combo;
      return (mode === 'WB') ? v.dim : v.size;
    }

    /* ---------------------------------------------------------------- */
    /* Beginstand bepalen                                                 */
    /* ---------------------------------------------------------------- */
    /* popular_option: sectie-instelling die als data-apdpc-popular beschikbaar is
     * op het hero-element of de picker-wrapper. Valt terug op de eerste beschikbare. */
    var popularOpt = (hero.getAttribute('data-apdpc-popular') || '').trim();

    /* W4. default_option: WELKE variant bij een verse load geselecteerd staat.
     *
     * 🟥 Dit is NIET popularOpt. popular_option stuurt de "Most picked"-badge en
     * de auto-snap na een maatwissel (C74/C78); default_option stuurt alleen de
     * beginstand. Op de WB lopen ze bewust uiteen: open op Twin 12, badge op
     * Full 15.
     *
     * Waarom dit hier moet en niet alleen in Liquid: initHero() eindigt met
     * paint(cur), en paint() schrijft prijs, buybar, ATC-ids en __apdpVariant.
     * Een puur server-side default zou dus binnen één frame teruggesnapt worden
     * naar de populaire variant -- de server-render zou zichtbaar zijn en dan
     * verspringen. Precies wat we niet willen: dat kost CLS.
     *
     * Leeg attribuut = deze tak doet niets en de volgorde eronder is byte-voor-
     * byte de oude. Onbekende of uitverkochte string idem: findWhere geeft null
     * en we vallen door naar de bestaande regels. */
    var defaultOpt = (hero.getAttribute('data-apdpc-default') || '').trim();

    var cur = null;

    /* Match op 'opt' EN op 'label': bij twee assen (cover, kids-cotton) is `opt`
     * de volle titel, bij WB/SIZE1 de optiestring. Dezelfde tweeledige test als
     * de Liquid-kant, zodat server-render en eerste paint dezelfde variant kiezen. */
    if (defaultOpt) {
      cur = findWhere(matrix, function (v) {
        return (v.opt === defaultOpt || v.label === defaultOpt) && v.avail;
      });
    }

    if (cur) {
      /* Gevonden: geen verdere regels. */
    } else if (mode === 'NONE') {
      cur = matrix[0] || null;
    } else if (mode === 'WB') {
      /* Auto-snap naar popular_option ('60"x80" 15lb' standaard, §2.6).
       * Bron: aricove-pdp-picker.liquid r.67-68. */
      if (popularOpt) {
        cur = findWhere(matrix, function (v) { return v.opt === popularOpt && v.avail; });
      }
      if (!cur) cur = findWhere(matrix, function (v) { return v.avail; }) || matrix[0] || null;
    } else if (mode === 'COLOR') {
      /* Initieel de eerste beschikbare variant. */
      cur = findWhere(matrix, function (v) { return v.avail; }) || matrix[0] || null;
    } else {
      /* SIZE1 */
      cur = findWhere(matrix, function (v) { return v.avail; }) || matrix[0] || null;
    }

    if (!cur && matrix.length === 0) {
      /* Geen varianten gevonden: geen picker, geen broadcast. */
      return;
    }

    // Shopify resolves valid ?variant= links, including unavailable variants.
    // Keep that server selection; merchandising defaults apply only without a link.
    var defaultVariant = cur;
    var initialId = hero.getAttribute('data-apdpc-initial-variant');
    var initialVariant = findWhere(matrix, function (v) { return String(v.id) === initialId; });
    if (initialVariant) cur = initialVariant;

    /* ---------------------------------------------------------------- */
    /* Selectie-staat                                                     */
    /* ---------------------------------------------------------------- */
    /* sel houdt de actieve waarden per as bij. resolve() zoekt de variant
     * die aan alle assen voldoet. */
    var sel = {};
    if (cur) {
      if (mode === 'WB')    { sel.dim = cur.dim; sel.lb = cur.lb; }
      if (mode === 'COLOR') { sel.color = cur.color; sel.size = cur.size; }
      if (mode === 'SIZE1') { sel.size = cur.size; }
    }

    /* ---------------------------------------------------------------- */
    /* Popular-gewicht per maat (WB auto-snap)                           */
    /* Bron: aricove-pdp-picker.liquid r.64 + CONTRACT §7.              */
    /* ---------------------------------------------------------------- */
    /* De popular_option-string bevat de meest-gekozen combo; we leiden
     * het populaire gewicht per maat af uit alle rijen met dat gewicht. */
    var POPULAR_LB = {};
    if (mode === 'WB') {
      /* Product defaults, kept in one map so every size gets a meaningful badge
       * and auto-snap even though `popular_option` can name only one combo. */
      POPULAR_LB['48"x72"'] = 12;
      POPULAR_LB['60"x80"'] = 15;
      POPULAR_LB['80"x87"'] = 20;
      POPULAR_LB['88"x104"'] = 22;

      /* C78. De sectie-instelling `popular_lb` overschrijft de defaults hierboven.
       * Bron is het attribuut dat c-picker op de wrapper zet, zodat de badge in de
       * DOM en de auto-snap hier gegarandeerd dezelfde tabel lezen. Ontbreekt het
       * attribuut (setting leeg), dan blijven de vier waarden hierboven staan en is
       * het gedrag identiek aan vandaag. */
      var poplbEl = document.querySelector('[data-apdpc-poplb]');
      if (poplbEl) {
        try {
          var poplbRaw = JSON.parse(poplbEl.getAttribute('data-apdpc-poplb') || '{}');
          for (var pk in poplbRaw) {
            if (!Object.prototype.hasOwnProperty.call(poplbRaw, pk)) continue;
            var pv = parseInt(poplbRaw[pk], 10);
            /* Alleen echte getallen overnemen; een typefout in de setting mag de
             * ingebouwde waarde niet met NaN overschrijven. */
            if (!isNaN(pv)) POPULAR_LB[pk] = pv;
          }
        } catch (e) {}
      }
      /* Lees popular_option; extraheer dim en lb. */
      var pm = String(popularOpt).match(/^(.*?)\s+(\d+)\s*lb$/i);
      if (pm) {
        /* Bewaar als fallback-populair voor bekende maatfamilies.
         * De picker-snippet gebruikte hardcoded Twin 12 / Full 15 / Queen 20 / King 22;
         * hier leiden we het af uit de popular_option instelling zodat Wilson het kan aanpassen. */
        POPULAR_LB[pm[1].trim()] = parseInt(pm[2], 10);
      }
      /* Vul overige maten in vanuit de matrix (eerste rij per maat met is_popular:true of
       * als fallback het laagste gewicht dat beschikbaar is). */
      eachDim(matrix).forEach(function (dim) {
        if (POPULAR_LB[dim] === undefined) {
          var rows = weightsFor(matrix, dim);
          /* Neem het beschikbare gewicht met de hoogste verkoopsfrequentie als geen
           * explicit popular veld aanwezig is. Hier: eerste beschikbare als fallback. */
          var pop = findWhere(rows, function (v) { return v.avail; }) || rows[0];
          if (pop) POPULAR_LB[dim] = pop.lb;
        }
      });
    }

    /* ---------------------------------------------------------------- */
    /* Hulpfuncties matrix                                               */
    /* ---------------------------------------------------------------- */

    function findWhere(arr, fn) {
      for (var i = 0; i < arr.length; i++) { if (fn(arr[i])) return arr[i]; }
      return null;
    }

    /* Geef alle unieke maten in de WB-matrix. */
    function eachDim(arr) {
      var seen = {}, out = [];
      for (var i = 0; i < arr.length; i++) {
        if (arr[i].dim && !seen[arr[i].dim]) { seen[arr[i].dim] = true; out.push(arr[i].dim); }
      }
      return out;
    }

    /* Varianten voor een specifieke maat, gesorteerd op gewicht. */
    function weightsFor(arr, dim) {
      return arr.filter(function (v) { return v.dim === dim; })
        .sort(function (a, b) { return a.lb - b.lb; });
    }

    /* Populaire gewicht-variant voor een maat (bij maatwissel auto-snap).
     * Bron: aricove-pdp-picker.liquid r.83-88. */
    function popularForDim(dim) {
      var rows = weightsFor(matrix, dim);
      var pl = POPULAR_LB[dim];
      return findWhere(rows, function (v) { return v.lb === pl && v.avail; })
          || findWhere(rows, function (v) { return v.avail; })
          || null;
    }

    /* ---------------------------------------------------------------- */
    /* Oplossen: geselecteerde variant vinden                             */
    /* ---------------------------------------------------------------- */
    function resolve() {
      if (mode === 'NONE') { return cur; }
      if (mode === 'WB') {
        return findWhere(matrix, function (v) {
          return v.dim === sel.dim && v.lb === sel.lb;
        }) || cur;
      }
      if (mode === 'COLOR') {
        return findWhere(matrix, function (v) {
          return v.color === sel.color && v.size === sel.size;
        }) || cur;
      }
      /* SIZE1 */
      return findWhere(matrix, function (v) { return v.size === sel.size; }) || cur;
    }

    /* ---------------------------------------------------------------- */
    /* Pickers synchroniseren (aria-pressed + disabled + unavailable)    */
    /* ---------------------------------------------------------------- */
    /* Alle pickers (inline + sheet) worden tegelijk bijgewerkt via aria-pressed.
     * Bron: CONTRACT §3, §7 "alle pickers via aria-pressed". */
    function syncPickers(v) {
      if (!v) return;

      /* Kleur-swatches */
      var swatches = document.querySelectorAll('[data-apdpc-color]');
      for (var i = 0; i < swatches.length; i++) {
        var sw = swatches[i];
        var match = sw.getAttribute('data-apdpc-color') === v.color;
        sw.setAttribute('aria-pressed', match ? 'true' : 'false');
      }
      var colorNames = document.querySelectorAll('[data-apdpc-color-name]');
      for (var i = 0; i < colorNames.length; i++) {
        colorNames[i].textContent = String(v.color || '').replace(/-/g, ' ');
      }

      /* Maat-tegels */
      var sizeBtns = document.querySelectorAll('[data-apdpc-size]');
      for (var i = 0; i < sizeBtns.length; i++) {
        var sb = sizeBtns[i];
        var sv = sb.getAttribute('data-apdpc-size');
        /* C84. Bij collapse draagt de tegel de volle optiestring; axisSize()
         * levert dan dezelfde sleutel uit de matrix. */
        var activeSize = axisSize(v);
        var smatch = sv === activeSize;
        sb.setAttribute('aria-pressed', smatch ? 'true' : 'false');

        /* Onbeschikbare maten markeren.
         * Controleer of er minstens één beschikbare variant in de matrix is voor deze maat. */
        var hasAvail = false;
        for (var j = 0; j < matrix.length; j++) {
          var vm = matrix[j];
          var vmSize = axisSize(vm);
          if (vmSize === sv && vm.avail) { hasAvail = true; break; }
        }
        if (!hasAvail) {
          sb.disabled = true;
          sb.setAttribute('data-apdpc-unavailable', '');
        } else {
          sb.disabled = false;
          sb.removeAttribute('data-apdpc-unavailable');
        }
      }

      /* Gewicht-pillen (WB) */
      /* C78. Het populaire gewicht van de ACTIEVE maat. Dit stond nergens in de
       * JS: de badge was puur Liquid en werd alleen voor de beginmaat berekend,
       * dus bij een maatwissel werd er nooit iets getoond of verborgen en hadden
       * Twin, Queen en King nooit een markering. `v.dim` en niet `sel.dim`, zodat
       * de badge ook klopt op het pad via window.__apdpc.set(id), waar sel pas ná
       * paint() wordt bijgewerkt. */
      var activePopLb = (mode === 'WB') ? POPULAR_LB[v.dim] : undefined;

      var wbtns = document.querySelectorAll('[data-apdpc-weight]');
      for (var i = 0; i < wbtns.length; i++) {
        var wb = wbtns[i];
        var wv = parseInt(wb.getAttribute('data-apdpc-weight'), 10);
        var wmatch = wv === v.lb;
        wb.setAttribute('aria-pressed', wmatch ? 'true' : 'false');

        /* Onbeschikbare gewichten voor de huidige maat uitschakelen. */
        var wAvail = findWhere(matrix, function (r) {
          return r.dim === v.dim && r.lb === wv && r.avail;
        });
        if (!wAvail) {
          wb.disabled = true;
          wb.setAttribute('data-apdpc-unavailable', '');
        } else {
          wb.disabled = false;
          wb.removeAttribute('data-apdpc-unavailable');
        }

        /* Badge volgt de maatkeuze. Voorwaarde is drieledig en elk deel doet werk:
         *   - het gewicht is het populaire gewicht van DEZE maat;
         *   - de tegel is beschikbaar voor deze maat (geen badge op een
         *     uitgeschakelde tegel);
         *   - er is überhaupt een populair gewicht bekend.
         * Elke picker op de pagina (inline + sheet) loopt door dezelfde lus, dus
         * ze blijven per definitie gelijk -- en omdat elke tegel expliciet wordt
         * gezet, kan er nooit een tweede badge blijven staan.
         * Toets in de browser op computed style, niet op het attribuut
         * (CONTRACT §1.4). */
        var popEl = wb.querySelector('.apdpc-weight__pop');
        if (popEl) {
          popEl.hidden = !(activePopLb !== undefined && wv === activePopLb && !!wAvail);
        }
      }
    }

    /* ---------------------------------------------------------------- */
    /* Prijs bijwerken                                                    */
    /* ---------------------------------------------------------------- */
    /* §2.8: was-prijs rood doorgestreept, Save-badge rood.
     * Sale-guard: compare > price aan beide kanten (kids: compare < price op elke variant;
     * guard voorkomt een vals "Save"-label). */
    function renderPrice(v) {
      if (!v) return;

      var nowEl  = document.querySelector('[data-apdpc-price-now]');
      var wasEl  = document.querySelector('[data-apdpc-price-was]');
      var saveEl = document.querySelector('[data-apdpc-price-save]');
      var bbPriceEl = document.querySelector('[data-apdpc-bb-price]');
      var bbWasEl   = document.querySelector('[data-apdpc-bb-was]');
      var bbSaveEl  = document.querySelector('[data-apdpc-bb-save]');

      var formatted = money(v.price);
      if (nowEl)     nowEl.textContent  = formatted;
      if (bbPriceEl) bbPriceEl.textContent = formatted;

      var onSale = v.compare && v.compare > v.price;
      if (wasEl) {
        if (onSale) { wasEl.textContent = money(v.compare); wasEl.hidden = false; }
        else wasEl.hidden = true;
      }
      if (saveEl) {
        if (onSale) { saveEl.textContent = 'Save ' + money(v.compare - v.price); saveEl.hidden = false; }
        else saveEl.hidden = true;
      }
      if (bbWasEl) {
        if (onSale) { bbWasEl.textContent = money(v.compare); bbWasEl.hidden = false; }
        else bbWasEl.hidden = true;
      }
      /* Spiegel van saveEl voor de buybar (BUYBAR-REDESIGN 18-09). */
      if (bbSaveEl) {
        if (onSale) { bbSaveEl.textContent = 'Save ' + money(v.compare - v.price); bbSaveEl.hidden = false; }
        else bbSaveEl.hidden = true;
      }
    }

    /* ---------------------------------------------------------------- */
    /* Galerij filteren en dots/teller herbouwen                         */
    /* ---------------------------------------------------------------- */
    /* Schuift de track-slides op basis van data-apdpc-msize en data-apdpc-mcolor.
     * Bron (maat): aricove-pdp-hero.liquid r.279-316, aricove-pdp-m-gallery.liquid C53.
     * Bron (kleur): aricove-pdp-cover-hero.liquid r.394-424, aricove-pdp-m-gallery.liquid C22.
     *
     * Regel: slide met leeg token -> altijd zichtbaar (!token guard).
     * Maat: token split op '-', match als key er in zit (twin-full dekt twin en full).
     * Kleur: exact gelijk.
     * Volgorde: eerst maat (WB), dan kleur (cover/kids); beide guards zijn orthogonaal. */
    /* Mobiel paginationcontract: tot en met 7 beelden directe dots; vanaf 8 alleen
     * de compacte teller. Acht 24px-taptargets maken anders een grote of gewrapte
     * capsule over het productbeeld (Kids, Cover, Bed, Body Pillow en Fill). */
    var DOT_MAX = 7;

    function filterGallery(v) {
      if (!v) return;

      var key    = null;
      var color  = null;

      if (mode === 'WB' && v.dim) {
        key = DIM_TO_KEY[v.dim] || null;
      }
      /* SIZE1 (bed: Queen/King). Zonder deze tak bleef key null en filterde de
       * galerij NIET: met King geselecteerd bleef de queen-foto staan. Dat is een
       * regressie t.o.v. de huidige bed-PDP, die dit wel doet
       * (aricove-pdp-bed-hero.liquid, syncGallery r.306-334).
       * De alt-token is lowercase (#size_queen), de variantoptie is 'Queen' -> lowercasen.
       * Daarna dezelfde token-match als WB, zodat een gedeeld token (queen-king) blijft werken. */
      if (mode === 'SIZE1' && v.size) {
        key = String(v.size).toLowerCase();
      }
      if ((mode === 'COLOR') && v.color) {
        color = v.color;
      }

      /* Desktop-rail (data-apdpc-thumb) */
      var thumbs = document.querySelectorAll('[data-apdpc-thumb]');
      var firstVisible = null;
      for (var i = 0; i < thumbs.length; i++) {
        var t = thumbs[i];
        var ms = t.getAttribute('data-apdpc-msize') || '';
        var mc = t.getAttribute('data-apdpc-mcolor') || '';

        var sizeOk  = !key   || !ms || (ms.split('-').indexOf(key) !== -1);
        var colorOk = !color || !mc || mc === color;
        var show = sizeOk && colorOk;
        t.hidden = !show;
        if (show && !firstVisible) firstVisible = t;
      }

      /* Rij-slides (de track, voor mobiel scroll-snap en als bron voor dots/teller). */
      var firstSlide = null;
      var track = document.querySelector('[data-apdpc-gallery-track]');
      if (track) {
        var allSlides = track.querySelectorAll('.apdpc-gallery__slide');
        var visibleCount = 0;
        for (var i = 0; i < allSlides.length; i++) {
          var sl = allSlides[i];
          var sms = sl.getAttribute('data-apdpc-msize') || '';
          var smc = sl.getAttribute('data-apdpc-mcolor') || '';
          var sizeOk2  = !key   || !sms || (sms.split('-').indexOf(key) !== -1);
          var colorOk2 = !color || !smc || smc === color;
          var show2 = sizeOk2 && colorOk2;
          sl.hidden = !show2;
          if (show2) {
            visibleCount++;
            if (!firstSlide) firstSlide = sl;
          }
        }
        /* Dots en teller herbouwen na filtering. */
        rebuildDots(track, visibleCount);
      }

      /* Rijden de main-stage naar de eerste zichtbare thumb (desktop) of slide (mobiel). */
      driveMain(firstVisible, v);
    }

    /* Bouwt dots opnieuw op na een kleur- of maatwissel.
     * Bron: aricove-pdp-m-gallery.liquid r.148-168 (herbouwDots). */
    function rebuildDots(track, n) {
      var dotsWrap = document.querySelector('[data-apdpc-gallery-dots]');
      var countEl  = document.querySelector('[data-apdpc-gallery-count]');

      if (countEl) {
        if (n > DOT_MAX) { countEl.textContent = '1 / ' + n; countEl.hidden = false; }
        else countEl.hidden = true;
      }
      if (!dotsWrap) return;

      dotsWrap.innerHTML = '';
      dotsWrap.hidden = (n > DOT_MAX);
      if (n > DOT_MAX) return;
      var visibleSlides = track ? track.querySelectorAll('.apdpc-gallery__slide:not([hidden])') : [];

      for (var i = 0; i < n; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'apdpc-gallery__dot' + (i === 0 ? ' is-active' : '');
        var img = visibleSlides[i] && visibleSlides[i].querySelector('img[alt]');
        var imgAlt = img ? img.getAttribute('alt').trim() : '';
        b.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + n + (imgAlt ? ': ' + imgAlt : ''));
        if (i === 0) b.setAttribute('aria-current', 'true');
        (function (idx) {
          b.addEventListener('click', function () {
            if (track) {
              /* Scroll naar de zichtbare slide op positie idx.
               * Niet-zichtbare slides hebben display:none, dus scrollLeft-offset = idx * breedte. */
              track.scrollTo({ left: idx * track.clientWidth, behavior: 'smooth' });
            }
            activateDot(dotsWrap, idx, n);
          });
        })(i);
        dotsWrap.appendChild(b);
      }

      /* Reset scroll naar het begin en activeer dot 0. */
      if (track) track.scrollLeft = 0;
      activateDot(dotsWrap, 0, n);
    }

    function activateDot(dotsWrap, idx, total) {
      if (!dotsWrap) return;
      var allDots = dotsWrap.querySelectorAll('.apdpc-gallery__dot');
      for (var i = 0; i < allDots.length; i++) {
        allDots[i].classList.toggle('is-active', i === idx);
        if (i === idx) allDots[i].setAttribute('aria-current', 'true');
        else allDots[i].removeAttribute('aria-current');
      }
      var countEl = document.querySelector('[data-apdpc-gallery-count]');
      if (countEl) countEl.textContent = (idx + 1) + ' / ' + total;
    }

    /* Rijdt de main-stage aan vanuit firstVisible thumb of variant-beeld. */
    function driveMain(firstVisible, v) {
      var stage = document.querySelector('.apdpc-gallery__stage');
      if (!stage) return;
      var mainSlide = stage.querySelector('.apdpc-gallery__slide:not([hidden])');
      /* Op ≥768px is er geen scrollende track; de rail (thumbs) stuurt de stage. */
      var thumbs = document.querySelectorAll('[data-apdpc-thumb]');
      for (var i = 0; i < thumbs.length; i++) {
        var t = thumbs[i];
        var isFirst = !t.hidden && (firstVisible ? t === firstVisible : i === 0);
        t.setAttribute('aria-pressed', isFirst ? 'true' : 'false');
      }
    }

    /* Scroll-listener voor dots synchroniseren.
     * Bron: aricove-pdp-m-gallery.liquid r.223-229. */
    function bindTrackScroll() {
      var track  = document.querySelector('[data-apdpc-gallery-track]');
      var dotsWrap = document.querySelector('[data-apdpc-gallery-dots]');
      if (!track || !dotsWrap) return;
      track.addEventListener('scroll', function () {
        if (!track.clientWidth) return;
        var idx = Math.round(track.scrollLeft / track.clientWidth);
        var total = track.querySelectorAll('.apdpc-gallery__slide:not([hidden])').length || 1;
        activateDot(dotsWrap, idx, total);
      });
    }

    /* Thumb-klik op desktop rijdt de stage aan.
     * Bron: aricove-pdp-cover-hero.liquid r.448-454 (thumb manual swap).
     *
     * 🟥 BUG 25-08 — HIER STOND EEN CONTRACT WAARVAN ÉÉN HELFT NOOIT IS GESCHREVEN.
     * Deze functie zette alleen `aria-pressed`, met erboven het comment "op ≥768px
     * toont de stage de geselecteerde slide via CSS op de actieve thumb; CSS pikt
     * dat op". Die CSS heeft nooit bestaan: de enige aria-pressed-regel in de hele
     * bundel is `.apdpc-gallery__thumb[aria-pressed="true"]{border-color:...}` en
     * die kleurt de RAND van de thumb. Geen `:has()`, geen broer-selector, geen
     * transform op de track. Gevolg op desktop, waar
     * `.apdpc-gallery__track{overflow-x:hidden}` staat terwijl elke slide
     * `flex:0 0 100%` is: het venster blijft op scrollLeft 0 hangen en je ziet
     * altijd slide 1. Gat sinds de bouw (1d4f825, R0, 21-08), geen regressie.
     * Gemeten op alle zeven live PDP's, 1440x900: 11 van 88 thumbklikken juist —
     * en die 11 waren telkens thumb 0, die al op scrollLeft 0 stond.
     *
     * DE OPLOSSING: de JS verzet de track, want dat mechanisme bestond al. Zie
     * `bindTrackScroll()`, dat `scrollLeft / clientWidth` al terugrekent naar een
     * dotindex. `overflow-x:hidden` blokkeert PROGRAMMATISCH scrollen niet, dus dit
     * werkt op desktop. En het geeft één codepad voor mobiel en desktop.
     *
     * 🟥 DE INDEX TELT OVER DE ZICHTBARE SLIDES, NIET OVER ALLE. De variant-JS zet
     * `hidden` op thumbs én slides per maat/kleur, en `hidden` geeft `display:none`:
     * verborgen slides vallen uit de flex-rij. Gemeten op kids-cotton/1440: 24
     * slides, 8 zichtbaar, scrollWidth 4455 = 8 x 557, offsets 0/557/1114/...
     * Het `data-apdpc-thumb`-attribuut draagt de RUWE loopindex (forloop.index0) en
     * is dus onbruikbaar als scrolldoel: op kids-cotton/Jungle-Green zijn de
     * zichtbare thumbs 16..23, en `16 * clientWidth` scrollt voorbij het einde van
     * de track. Deze bug is alleen ná een variantwissel zichtbaar en komt door een
     * oppervlakkige test heen. We tellen daarom de positie onder de niet-verborgen
     * thumbs, en pakken de zichtbare SLIDE op diezelfde positie.
     *
     * 🟥 EN WE SCROLLEN NAAR DE GEMETEN `offsetLeft`, NIET NAAR `pos * clientWidth`.
     * Die twee lopen door subpixelafronding uiteen (gemeten: 2784 tegen 2785 bij
     * clientWidth 557). Het element zelf is de bron van waarheid; een berekening
     * die er één pixel naast zit, laat een streepje van de buurslide staan.
     * `behavior:'auto'` — de rail is een picker, geen carrousel; een smooth scroll
     * over 3.900 px is trage bediening, en op mobiel doet de vinger dit al. */
    function bindThumbClicks() {
      var thumbs = document.querySelectorAll('[data-apdpc-thumb]');
      for (var i = 0; i < thumbs.length; i++) {
        (function (thumb) {
          thumb.addEventListener('click', function () {
            var all = document.querySelectorAll('[data-apdpc-thumb]');
            var pos = -1;
            var zichtbaar = 0;
            for (var j = 0; j < all.length; j++) {
              all[j].setAttribute('aria-pressed', 'false');
              if (!all[j].hidden) {
                if (all[j] === thumb) pos = zichtbaar;
                zichtbaar++;
              }
            }
            thumb.setAttribute('aria-pressed', 'true');

            /* Verzet de track naar de zichtbare slide op dezelfde positie. */
            if (pos < 0) return;
            var track = document.querySelector('[data-apdpc-gallery-track]');
            if (!track) return;
            var slides = track.querySelectorAll('.apdpc-gallery__slide:not([hidden])');
            var doel = slides[pos];
            if (!doel) return;
            track.scrollTo({ left: doel.offsetLeft - track.offsetLeft, behavior: 'auto' });

            /* Dots meelopen. Op desktop staan ze op display:none, maar de teller
             * "N / M" niet — en op de smalle kant van het breekpunt dragen ze de
             * navigatie. `bindTrackScroll()` doet dit ook al bij een scroll-event;
             * dit is de vangnet voor het geval de track niet hoeft te bewegen. */
            var dotsWrap = document.querySelector('[data-apdpc-gallery-dots]');
            if (dotsWrap) activateDot(dotsWrap, pos, slides.length || 1);
          });
        })(thumbs[i]);
      }
    }

    /* Uitgestelde slides (data-apdpc-defer, c-gallery): zichtbare slide 3+ krijgt
     * pas bij de eerste interactie met de galerij of de eerste variantwissel zijn
     * echte src/srcset. Daarna beslist `loading="lazy"` weer, dus verborgen slides
     * laden nog steeds niet. Eén keer per pagina; de listeners doen daarna niets. */
    var firstPaintDone = false;
    var galleryDeferred = !!hero.querySelector('img[data-apdpc-defer]');
    function hydrateGallery() {
      if (!galleryDeferred) return;
      galleryDeferred = false;
      var imgs = hero.querySelectorAll('img[data-apdpc-defer]');
      for (var i = 0; i < imgs.length; i++) {
        var im = imgs[i];
        var ss = im.getAttribute('data-apdpc-srcset');
        var s = im.getAttribute('data-apdpc-src');
        if (ss) im.setAttribute('srcset', ss);
        if (s) im.setAttribute('src', s);
        im.removeAttribute('data-apdpc-srcset');
        im.removeAttribute('data-apdpc-src');
        im.removeAttribute('data-apdpc-defer');
      }
    }
    function bindGalleryDefer() {
      if (!galleryDeferred) return;
      var gallery = hero.querySelector('[data-apdpc-gallery]');
      if (!gallery) return;
      /* mouseover = intentie op desktop (thumbs, stage); touchstart/pointerdown vóór de
       * swipe beweegt; scroll vangt een swipe of dot-klik; focusin/keydown het toetsenbord. */
      var evs = ['pointerdown', 'touchstart', 'mouseover', 'focusin', 'keydown', 'wheel'];
      for (var i = 0; i < evs.length; i++) {
        gallery.addEventListener(evs[i], hydrateGallery, { passive: true });
      }
      var track = gallery.querySelector('[data-apdpc-gallery-track]');
      if (track) track.addEventListener('scroll', hydrateGallery, { passive: true });
    }

    /* ---------------------------------------------------------------- */
    /* Voorraad + leverdatum                                              */
    /* ---------------------------------------------------------------- */
    /* Bron: aricove-pdp-hero.liquid r.350-382, aricove-pdp-kids-hero.liquid r.590-622.
     * Leest invMap uit data-apdpc-stock op het delivery-element.
     * Real tracked inventory only -- geen synthetische schaarste (D-0021). */
    function buildInvMap() {
      var stockEl = document.querySelector('[data-apdpc-stock]');
      if (!stockEl) return {};
      var map = {};
      try {
        var raw = stockEl.getAttribute('data-apdpc-inv') || '[]';
        var arr = JSON.parse(raw);
        for (var i = 0; i < arr.length; i++) { map[arr[i].id] = arr[i]; }
      } catch (e) {}
      return map;
    }

    var invMap = buildInvMap();

    function renderStock(v) {
      var stockEl = document.querySelector('[data-apdpc-stock]');
      if (!stockEl) return;
      var threshold = parseInt(stockEl.getAttribute('data-apdpc-threshold') || '10', 10);
      if (isNaN(threshold)) threshold = 10;

      var labelEl  = stockEl.querySelector('[data-apdpc-stock-label]');
      var extraEl  = stockEl.querySelector('[data-apdpc-stock-extra]');

      var rec     = (v && invMap[v.id]) ? invMap[v.id] : null;
      var avail   = rec ? rec.avail : (v ? v.avail : true);
      var tracked = !!(rec && rec.track);
      var inv     = rec ? rec.inv : null;

      stockEl.classList.remove('stock--low', 'stock--oos');

      if (avail === false || (tracked && inv !== null && inv <= 0)) {
        stockEl.classList.add('stock--oos');
        if (labelEl) labelEl.textContent = 'Out of stock';
        if (extraEl) { extraEl.hidden = false; extraEl.textContent = 'Contact support for stock-ready estimates'; }
      } else if (tracked && inv !== null && inv < threshold) {
        stockEl.classList.add('stock--low');
        if (labelEl) labelEl.textContent = 'Low in stock';
        if (extraEl) { extraEl.hidden = true; extraEl.textContent = ''; }
      } else {
        if (labelEl) labelEl.textContent = 'In stock';
        if (extraEl) { extraEl.hidden = true; extraEl.textContent = ''; }
      }
    }

    /* Leverdatum invullen. Venster wordt gelezen uit data-apdpc-ship-min/max.
     * Bron: aricove-pdp-hero.liquid r.336-345 (WB: +2..+5), kids-hero r.582-587 (+2..+7). */
    function renderDelivery() {
      var de = document.querySelector('[data-apdpc-deldate]');
      if (!de) return;
      try {
        var shipMin = parseInt(de.getAttribute('data-apdpc-ship-min') || '2', 10);
        var shipMax = parseInt(de.getAttribute('data-apdpc-ship-max') || '5', 10);
        var now = new Date();
        var addCal = function (n) {
          var d = new Date(now); d.setDate(d.getDate() + n); return d;
        };
        var fmt = function (d) {
          return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        };
        de.textContent = fmt(addCal(shipMin)) + ' – ' + fmt(addCal(shipMax));
      } catch (e) {}
    }

    /* ---------------------------------------------------------------- */
    /* Buybar                                                             */
    /* ---------------------------------------------------------------- */
    /* Bijwerken van de buybar-inhoud. Zichtbaarheid via IntersectionObserver hieronder.
     * Bron: CONTRACT §4, §7 "buybar bijwerken". */
    function renderBuybar(v) {
      if (!v) return;
      var variantBtn = document.querySelector('[data-apdpc-buybar-open]');
      if (!variantBtn) return;
      var label = v.label || v.opt || '';
      /* Schrijf in de sub-span, NIET in de knop zelf. textContent op de knop wist al
       * zijn kinderen -- en daar zitten de prijs- en was-elementen in die de server
       * correct rendert en die renderPrice() hierboven bijwerkt. Gemeten op de
       * fill-preview 21-08: de buybar toonde alleen "7oz" en Add to cart, zonder prijs,
       * op een product dat op dat moment in de sale stond ($24.95 van $29.95). */
      var sub = variantBtn.querySelector('[data-apdpc-bb-sub]');
      if (sub) sub.textContent = label;
      else variantBtn.textContent = label; /* oudere markup zonder sub-span */
    }

    /* C87. renderBuybarSelectors() en bindBuybarSelectors() stonden hier.
     * Ze vulden en synchroniseerden de <select>-velden in
     * het selectorblok van de balk. Dat blok is in C87 uit c-buybar.liquid
     * verwijderd -- niet verborgen -- omdat de variantknop hetzelfde doet en
     * het complete pickerpaneel opent. Zonder markup is er niets te vullen,
     * dus zijn beide functies en hun aanroepen in paint() en de init mee
     * verwijderd in plaats van als dode code te blijven staan.
     *
     * renderBuybar() hierboven BLIJFT: die schrijft het variantlabel in
     * [data-apdpc-bb-sub] binnen de knop en is nu de enige plek waar de balk
     * laat zien welke variant in het mandje gaat.
     *
     * setAxis() op window.__apdpc blijft ook bestaan: dat is publieke API
     * (CONTRACT §7) en had hier alleen zijn laatste interne aanroeper. */

    /* ---------------------------------------------------------------- */
    /* ATC-knop bijwerken                                                 */
    /* ---------------------------------------------------------------- */
    function renderAtc(v) {
      if (!v) return;
      /* Alle ATC-knoppen op de pagina (hero + picker-sheet + buybar). */
      var atcBtns = document.querySelectorAll('[data-apdpc-atc]');
      for (var i = 0; i < atcBtns.length; i++) {
        var btn = atcBtns[i];
        btn.setAttribute('data-variant-id', String(v.id));
        btn.disabled = !v.avail;

        /* Hero en picker-sheet dragen de prijs ("Add to cart — $96.95"). Alleen
         * de prijs-SPAN wordt herschreven, niet de knoptekst:
         * btn.textContent zetten zou "Add to cart" wissen en de span slopen.
         * De buybar-ATC heeft geen prijs-span (daar staat de prijs links) en
         * slaat deze tak dus stil over. */
        var priceSpan = btn.querySelector('[data-apdpc-cta-price]');
        if (priceSpan && v.avail) { priceSpan.textContent = moneyFull(v.price); }
      }
    }

    /* Officiële Shop Pay Installments-banner leest de actieve variant uit het
     * product-formulier. De component-PDP gebruikt AJAX-ATC en heeft daarom een
     * tweede, kleine product-form bij de termijnregel. Shopify vereist dat het
     * hidden name=id-veld meebeweegt en een change-event krijgt. */
    function renderPaymentTerms(v) {
      if (!v) return;
      var inputs = document.querySelectorAll('[data-apdpc-payment-terms] input[name="id"]');
      for (var i = 0; i < inputs.length; i++) {
        inputs[i].value = String(v.id);
        inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ---------------------------------------------------------------- */
    /* Broadcast                                                          */
    /* ---------------------------------------------------------------- */
    /* Publiek contract: zet window.__apdpVariant en dispatch 'apdp:variant'.
     * Payload = superset van alle families (CONTRACT §7, §2.6). */
    function broadcast(v) {
      if (!v) return;
      var detail = {
        id:      v.id,
        opt:     v.opt   || v.label || null,
        price:   v.price,
        compare: v.compare || 0,
        label:   v.label  || v.opt  || null,
        avail:   v.avail,
        dim:     v.dim   || null,
        lb:      v.lb    || null,
        color:   v.color || null,
        inv:     v.inv   || null
      };
      window.__apdpVariant = detail;
      document.dispatchEvent(new CustomEvent('apdp:variant', { detail: detail }));
    }

    /* ---------------------------------------------------------------- */
    /* Hoofd-rendercyclus                                                 */
    /* ---------------------------------------------------------------- */
    function paint(v, updateUrl) {
      if (!v) return;
      /* De eerste paint zet alleen de beginvariant; elke volgende is een wissel
       * en kan uitgestelde slides zichtbaar maken. */
      if (firstPaintDone) hydrateGallery();
      firstPaintDone = true;
      cur = v;
      syncPickers(v);
      renderPrice(v);
      filterGallery(v);
      renderStock(v);
      renderBuybar(v);
      renderAtc(v);
      renderPaymentTerms(v);
      if (updateUrl !== false && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', String(v.id));
        window.history.replaceState(window.history.state, '', url.toString());
      }
      broadcast(v);
    }

    /* ---------------------------------------------------------------- */
    /* Picker-klikkers binden                                             */
    /* ---------------------------------------------------------------- */
    /* Alle kleur-swatches (op elke picker -- inline en sheet tegelijk). */
    function bindColorClicks() {
      var swatches = document.querySelectorAll('[data-apdpc-color]');
      for (var i = 0; i < swatches.length; i++) {
        (function (sw) {
          sw.addEventListener('click', function () {
            sel.color = sw.getAttribute('data-apdpc-color');
            var v = resolve();
            if (v) paint(v);
          });
        })(swatches[i]);
      }
    }

    /* Maat-tegels (mode COLOR en SIZE1). */
    function bindSizeClicks() {
      var sizeBtns = document.querySelectorAll('[data-apdpc-size]');
      for (var i = 0; i < sizeBtns.length; i++) {
        (function (sb) {
          sb.addEventListener('click', function () {
            if (sb.disabled) return;
            var sv = sb.getAttribute('data-apdpc-size');
            /* C84. Gecollapste tegel: de waarde is de volle optiestring en legt
             * BEIDE assen tegelijk vast. Geen auto-snap nodig -- er is per maat
             * maar één gewicht, dat is precies waarom de stap is samengevoegd. */
            if (collapsed) {
              var combo = findWhere(matrix, function (r) { return r.combo === sv; });
              if (!combo) return;
              sel.dim = combo.dim;
              sel.lb = combo.lb;
              paint(combo);
              return;
            }
            if (mode === 'WB') {
              /* WB: maat-as is dim; auto-snap gewicht. */
              var snapped = popularForDim(sv);
              if (!snapped) return;
              sel.dim = snapped.dim;
              sel.lb = snapped.lb;
              paint(snapped);
              return;
            } else {
              sel.size = sv;
            }
            var v = resolve();
            if (v) paint(v);
          });
        })(sizeBtns[i]);
      }
    }

    /* Gewicht-pillen (WB). Auto-snap wordt hier NIET toegepast; de gebruiker
     * kiest expliciet een gewicht. Bron: picker.liquid r.115. */
    function bindWeightClicks() {
      var wbtns = document.querySelectorAll('[data-apdpc-weight]');
      for (var i = 0; i < wbtns.length; i++) {
        (function (wb) {
          wb.addEventListener('click', function () {
            if (wb.disabled) return;
            sel.lb = parseInt(wb.getAttribute('data-apdpc-weight'), 10);
            var v = resolve();
            if (v) paint(v);
          });
        })(wbtns[i]);
      }
    }

    /* ---------------------------------------------------------------- */
    /* apdp:setvariant luisteren (sticky bar -> picker)                  */
    /* ---------------------------------------------------------------- */
    /* Bron: picker.liquid r.144-146, cover-hero r.460-474, kids-hero r.564-569. */
    document.addEventListener('apdp:setvariant', function (e) {
      var id = e.detail && e.detail.id;
      if (!id) return;
      var v = findWhere(matrix, function (r) { return String(r.id) === String(id); });
      if (!v) return;
      /* Selectie-staat bijwerken per mode. */
      if (mode === 'WB')    { sel.dim = v.dim; sel.lb = v.lb; }
      if (mode === 'COLOR') { sel.color = v.color; sel.size = v.size; }
      if (mode === 'SIZE1') { sel.size = v.size; }
      paint(v);
    });

    window.addEventListener('popstate', function () {
      var id = new URL(window.location.href).searchParams.get('variant');
      var v = findWhere(matrix, function (r) { return String(r.id) === id; }) || defaultVariant;
      if (!v) return;
      if (mode === 'WB')    { sel.dim = v.dim; sel.lb = v.lb; }
      if (mode === 'COLOR') { sel.color = v.color; sel.size = v.size; }
      if (mode === 'SIZE1') { sel.size = v.size; }
      paint(v, false);
    });

    /* ---------------------------------------------------------------- */
    /* Buybar-zichtbaarheid via IntersectionObserver                     */
    /* ---------------------------------------------------------------- */
    /* IntersectionObserver op de hero-ATC, NIET een scrollY-drempel.
     * Bron: CONTRACT §4 "drempels zijn per PDP anders en breken bij copy-wijzigingen". */
    function bindBuybarVisibility() {
      var buybar = document.querySelector('[data-apdpc-buybar]');
      if (!buybar) return;
      var heroAtc = hero.querySelector('[data-apdpc-atc]');
      if (!heroAtc) return;

      /* Twee dingen tegelijk, en de tweede is niet optioneel: naast [hidden] moet de
       * klasse `is-visible` mee. De mobiele CSS parkeert de bar op translateY(100%) en
       * laat hem pas terugkomen met .is-visible. Die klasse werd nergens gezet, dus op
       * mobiel stond de buybar permanent ONDER de vouw -- gemeten op de fill-preview
       * 21-08: position fixed, top 667 bij een viewporthoogte van 667. Op desktop viel
       * het niet op, want daar overschrijft de media query de transform naar 0. */
      var toon = function (aan) {
        buybar.hidden = !aan;
        buybar.classList.toggle('is-visible', aan);
      };

      if (typeof IntersectionObserver === 'undefined') {
        /* Geen IO-ondersteuning: buybar altijd zichtbaar. */
        toon(true);
        return;
      }
      var obs = new IntersectionObserver(function (entries) {
        /* Hidden als de hero-ATC zichtbaar is; zichtbaar als hij uit beeld scrollt. */
        toon(!entries[0].isIntersecting);
      }, { threshold: 0 });
      obs.observe(heroAtc);
    }

    /* ---------------------------------------------------------------- */
    /* Intern API                                                         */
    /* ---------------------------------------------------------------- */
    /* window.__apdpc = { get(), set(id), setAxis(axis, value) }
     * Bron: CONTRACT §7. */
    window.__apdpc = {
      get: function () { return cur; },
      set: function (id) {
        var v = findWhere(matrix, function (r) { return String(r.id) === String(id); });
        if (v) { paint(v); }
      },
      setAxis: function (axis, value) {
        /* C84. De gecollapste as draagt beide waarden in één string. */
        if (axis === 'combo') {
          var cv = findWhere(matrix, function (r) { return r.combo === value; });
          if (!cv) return;
          sel.dim = cv.dim;
          sel.lb = cv.lb;
          paint(cv);
          return;
        }
        if (axis === 'color') { sel.color = value; }
        if (axis === 'size')  { sel.size  = value; }
        if (axis === 'dim')   {
          var sn = popularForDim(value);
          if (!sn) return;
          sel.dim = sn.dim;
          sel.lb = sn.lb;
          paint(sn);
          return;
        }
        if (axis === 'lb')    { sel.lb    = parseInt(value, 10); }
        var v = resolve();
        if (v && v.avail) paint(v);
      }
    };

    /* ---------------------------------------------------------------- */
    /* Initialiseren                                                      */
    /* ---------------------------------------------------------------- */
    bindColorClicks();
    bindSizeClicks();
    bindWeightClicks();
    bindTrackScroll();
    bindThumbClicks();
    bindGalleryDefer();
    bindBuybarVisibility();
    renderDelivery();

    /* Eerste paint: seed de broadcast zodat sticky-bar en stock-regel
     * direct in sync starten ook als ze vóór dit script geregistreerd zijn.
     * Bron: cover-hero.liquid r.481-482 ("FB-2026-08-16: initial paint"). */
    if (cur) paint(cur, false);
  }

  /* ------------------------------------------------------------------ */
  /* Alle [data-apdpc-hero]-elementen op de pagina initialiseren         */
  /* ------------------------------------------------------------------ */
  function init() {
    var heroes = document.querySelectorAll('[data-apdpc-hero]');
    for (var i = 0; i < heroes.length; i++) {
      initHero(heroes[i]);
    }
  }

  /* defer garandeert dat de DOM al aanwezig is bij uitvoering.
   * Veiligheidsnetting voor edge cases (dynamisch geïnjecteerde pagina's). */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
